<template>
  <Card class="form-stepper-card sm:mt-16 box-shadow-none sm:box-shadow-sm sm:p-24">
    <h1 class="text-h2 centered mt-32 mb-16">Let's find the sitters closest to you</h1>
    <p class="text-body centered mb-24">We have sitters in 95% of U.S. neighborhoods.</p>
    <form>
      <div class="mb-32">
        <TextField
          id="zipCode"
          v-model="zipCode"
          labelName="ZIP code"
          name="postal-code"
          type="text"
          maxlength="5"
          pattern="[0-9]*"
          class="mb-24"
          inputmode="numeric"
          aria-label="Five-digit zip code"
          :status="helperStatus"
          :limit-input-to-pattern="true"
          :helperText="helperText"
        />
      </div>
      <div class="flex flex-row-reverse">
        <RouteStepperNextButton
          :stepData="reviewSection"
          :draftData="jobPostDraft"
          :validate="validate"
          @errors="errors = $event"
        />
      </div>
    </form>
  </Card>
</template>

<script lang="ts">
import { HelperStatus } from '@/helpers/status';
import TextField from '@/components/textfield/TextField.vue';
import Card from '@/components/card/Card.vue';

import { sendBrazeEvent } from '@/lib/braze';
import { JOB_POST_LOCATION_LOADED } from '@/lib/constants/braze_custom_events';
import { ReviewSection } from '@/components/route_stepper/types';
import RouteStepperNextButton from '@/components/route_stepper/RouteStepperNextButton.vue';
import { Location } from '@/store/modules/job/types';
import { JobPostDraft } from './types';
import { PropType } from 'vue';

declare let google: any;

interface Errors {
  zipCode?: string;
}

interface JobPostLocationData {
  zipCode: string;
  location: Location | null;
  errors: Errors;
}

export default {
  name: 'JobPostLocation',
  components: {
    Card,
    TextField,
    RouteStepperNextButton,
  },
  props: {
    draft: {
      type: Object as PropType<JobPostDraft>,
      required: true,
    },
  },
  watch: {
    zipCode() {
      this.resetValidation();
      this.location = null;

      if (this.isZipCodeValid) {
        this.setLocationFromZip();
      }
    },
  },
  data(): JobPostLocationData {
    return {
      zipCode: this.draft.zipCode || this.$store.getters['account/getZipCode'] || '',
      location: {
        x: this.draft.location?.x || this.$store.getters['account/getLocation']?.x || 0,
        y: this.draft.location?.y || this.$store.getters['account/getLocation']?.y || 0,
      },
      errors: {},
    };
  },
  computed: {
    helperStatus(): HelperStatus {
      if (!this.errors.zipCode) {
        return HelperStatus.Neutral;
      }

      return HelperStatus.Error;
    },
    helperText(): string {
      if (!this.errors.zipCode) {
        return 'Please enter a five-digit ZIP code.';
      }
      return this.errors.zipCode;
    },
    isZipCodeValid(): boolean {
      return this.zipCode.length === 5 && /^[0-9]+$/.test(this.zipCode);
    },
    jobPostDraft(): JobPostDraft {
      return {
        zipCode: this.zipCode,
        location: this.location,
      };
    },
    reviewSection(): ReviewSection {
      return {
        title: 'Location',
        content: `ZIP code: ${this.zipCode}`,
        editPath: this.$route.path,
      };
    },
  },
  created() {
    sendBrazeEvent(JOB_POST_LOCATION_LOADED);
  },
  methods: {
    resetValidation() {
      this.errors = {};
    },
    extractCoordinates(results: any[]): { x: number; y: number } | null {
      const zipResult = results.find((r) => r.types?.[0] === 'postal_code');
      const components = zipResult?.address_components;
      if (!zipResult || !components || components.length === 0) {
        return null;
      }

      const zipCode = components.find((c) => c.types.includes('postal_code'))?.short_name;
      const city = components.find((c) => c.types.includes('political'))?.long_name;
      const state = components.find((c) => c.types.includes('administrative_area_level_1'))?.short_name;

      if (zipCode && city && state) {
        return {
          x: zipResult.geometry.location.lng(),
          y: zipResult.geometry.location.lat(),
        };
      }

      return null;
    },
    zipToLocation(zipCode: string): Promise<{ x: number; y: number } | null> {
      return new Promise((resolve, reject) => {
        new google.maps.Geocoder().geocode({ address: zipCode, componentRestrictions: { country: 'US' } }, (res, status) => {
          if (status === 'OK') {
            const coordinates = this.extractCoordinates(res);
            resolve(coordinates);
          } else {
            reject(new Error('Geocoding failed'));
          }
        });
      });
    },
    async setLocationFromZip(): Promise<void> {
      try {
        this.location = await this.zipToLocation(this.zipCode);
      } catch (error) {
        console.error('Error fetching location:', error);
      }
    },

    validate(): Errors {
      const errors: Errors = {};

      if (this.zipCode.length < 5) {
        errors.zipCode = 'ZIP code is required. Enter a five digit ZIP code.';
      } else if (!this.location) {
        errors.zipCode = 'Unable to find location. Please check the ZIP code.';
      }

      return errors;
    },
  },
};
</script>

<style scoped lang="scss">
.form-stepper-card {
  @media (max-width: 480px) {
    background-color: #fbfbfb !important;
  }
}
</style>
